import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, X, Sparkles, Code, Zap, Crown } from "lucide-react";

const PricingTiers = () => {
  const handleGetStarted = (tier: string) => {
    const urls = {
      'Explorer': 'https://app.coderide.ai/auth/sign-up',
      'Creator': 'https://buy.stripe.com/3cI5kDcpjao013seO47N600',
      'Pro': 'https://buy.stripe.com/eVq9AT1KFbs47rQ9tK7N601',
      'Early Adopter': 'https://buy.stripe.com/00wfZh4WR7bOdQe9tK7N603'
    };
    window.open(urls[tier as keyof typeof urls], '_blank');
  };

  const pricingTiers = [
    {
      name: "Explorer",
      price: "Free",
      vatText: "Perfect for trying vibe coding",
      vatIcon: <Code className="h-3 w-3" />,
      description: "Perfect for exploring CodeRide and launch your first AI-optimized project.",
      features: [
        { name: "1 Project", included: true },
        { name: "40 AI-Enhanced Tasks*", included: true },
        { name: "Additional credits on demand", included: false }
      ],
      buttonText: "START FOR FREE",
      buttonVariant: "outline" as const,
      isCurrentPlan: false,
      popular: false
    },
    {
      name: "Creator",
      price: "€9",
      period: "/month",
      vatText: "For regular vibe coding sessions",
      vatIcon: <Sparkles className="h-3 w-3" />,
      description: "Scale your AI coding across unlimited projects and zero context window problems.",
      features: [
        { name: "Unlimited projects", included: true },
        { name: "100 AI-Enhanced Tasks/month", included: true },
        { name: "Additional credits on demand", included: true }
      ],
      buttonText: "GET STARTED",
      buttonVariant: "outline" as const,
      isCurrentPlan: false,
      popular: false
    },
    {
      name: "Pro",
      price: "€29",
      period: "/month",
      vatText: "For power users",
      vatIcon: <Zap className="h-3 w-3" />,
      description: "Professional-grade AI coding with unlimited projects and the amount of tasks needed for developing complex products.",
      features: [
        { name: "Unlimited projects", included: true },
        { name: "400 AI-Enhanced Tasks/month", included: true },
        { name: "Additional credits on demand", included: true }
      ],
      buttonText: "GET STARTED",
      buttonVariant: "outline" as const,
      isCurrentPlan: false,
      popular: false
    },
    {
      name: "Early Adopter",
      price: "€119",
      period: "for life",
      vatText: "Limited time - 200 seats available",
      vatIcon: <Crown className="h-3 w-3" />,
      description: "Lifetime access to all CodeRide features with unlimited projects and 2.000 AI tasks included.",
      features: [
        { name: "Unlimited projects", included: true },
        { name: "2.000 AI-Enhanced Tasks*", included: true },
        { name: "Additional credits on demand", included: true }
      ],
      buttonText: "GET STARTED",
      buttonVariant: "default" as const,
      isCurrentPlan: false,
      popular: true
    }
  ];

  return (
    <section className="section-padding bg-coderide-black">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {pricingTiers.map((tier, index) => (
            <Card 
              key={tier.name} 
              className={`relative flex flex-col h-full transition-all duration-300 hover:shadow-2xl hover:shadow-coderide-light-blue/20 hover:scale-[1.02] bg-gray-900/50 border-gray-800 ${
                tier.popular 
                  ? 'border-coderide-light-blue border-2 bg-gradient-to-b from-coderide-light-blue/15 to-gray-900/50 shadow-lg shadow-coderide-light-blue/10' 
                  : 'hover:border-coderide-light-blue/50'
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-coderide-light-blue text-coderide-black px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                    Most popular
                  </span>
                </div>
              )}
              
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold mb-2 text-left text-white">{tier.name}</CardTitle>
                <div className="mb-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">{tier.price}</span>
                    {tier.period && (
                      <span className="text-gray-400 text-sm">{tier.period}</span>
                    )}
                  </div>
                  {tier.vatText && (
                    <div className="flex items-center gap-1.5 text-xs text-coderide-light-blue mt-2 text-left font-medium bg-coderide-light-blue/10 px-2 py-1 rounded-md border border-coderide-light-blue/20 w-fit">
                      {tier.vatIcon}
                      <span>{tier.vatText}</span>
                    </div>
                  )}
                </div>
                <CardDescription className="text-sm leading-relaxed text-left text-gray-300">
                  {tier.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-sm text-white">What's included:</h4>
                  <ul className="space-y-2">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2 text-sm">
                        {feature.included ? (
                          <Check className="h-4 w-4 text-coderide-light-blue shrink-0" />
                        ) : (
                          <X className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                        <span className={feature.included ? 'text-gray-200' : 'text-gray-500 line-through'}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto">
                  <Button 
                    className={`w-full font-bold ${
                      tier.popular 
                        ? 'bg-coderide-light-blue hover:bg-coderide-dark-blue text-white border-transparent' 
                        : 'border-coderide-light-blue text-coderide-light-blue hover:bg-coderide-light-blue hover:text-white bg-transparent'
                    }`}
                    variant={tier.popular ? "default" : "outline"}
                    onClick={() => handleGetStarted(tier.name)}
                  >
                    {tier.buttonText}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Asterisk explanation */}
        <div className="text-center mt-8 max-w-4xl mx-auto">
          <p className="text-xs text-gray-500">
            *Una tantum - One-time allocation, does not renew monthly
          </p>
        </div>

        {/* Additional info section */}
        <div className="text-center mt-8 max-w-4xl mx-auto">
          <div className="bg-gray-900/30 rounded-xl p-6 mb-8 border border-gray-800">
            <h3 className="text-lg font-semibold text-white mb-3">All plans include:</h3>
            <p className="text-gray-300 text-sm mb-4">
              <strong className="font-semibold text-white">MCP integration</strong>, <strong className="font-semibold text-white">persistent context management</strong>, priority support during beta, and seamless IDE integration.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-coderide-light-blue" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span>Secure billing</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-coderide-light-blue" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>24/7 support</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-coderide-light-blue" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              <span>Easy upgrades</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingTiers;
